import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Upload, RotateCcw, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileVisibilityBadge } from './FileVisibility';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOURCES = { project: 'projects', customer: 'customers', employee: 'employees' };

const recordLabel = (type, r) => type === 'employee'
  ? ([r.first_name, r.last_name].filter(Boolean).join(' ') || r.name)
  : type === 'project' ? [r.project_number, r.name].filter(Boolean).join(' · ') : r.name;

const RecordPicker = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [q, setQ] = useState('');
  useEffect(() => {
    setRecords([]);
    axios.get(`${API}/${SOURCES[value.type]}`).then((r) => setRecords(r.data || [])).catch(() => {});
  }, [value.type]);
  const shown = records.filter((r) => (recordLabel(value.type, r) || '').toLowerCase().includes(q.toLowerCase())).slice(0, 200);
  return (
    <div className="flex flex-wrap gap-2">
      <Select value={value.type} onValueChange={(type) => onChange({ type, id: '' })}>
        <SelectTrigger className="h-9 w-36 text-xs" data-testid="restore-entity-type"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.keys(SOURCES).map((k) => <SelectItem key={k} value={k} data-testid={`restore-entity-type-${k}`}>{t(`fileVisibility.${k}`)}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input className="h-9 w-44 text-xs" placeholder={t('restoreFiles.searchRecord')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="restore-record-search" />
      <Select value={value.id} onValueChange={(id) => onChange({ ...value, id })}>
        <SelectTrigger className="h-9 w-64 text-xs" data-testid="restore-record-select"><SelectValue placeholder={t('restoreFiles.pickRecord')} /></SelectTrigger>
        <SelectContent>
          {shown.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`restore-record-${r.id}`}>{recordLabel(value.type, r)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
};

const RestoreRow = ({ item, checked, onToggle }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 text-xs px-2 py-1.5 border-b last:border-0" data-testid={`restore-row-${item.key}`}>
      <Checkbox checked={checked} disabled={!item.restorable} onCheckedChange={(on) => onToggle(!!on)} data-testid={`restore-select-${item.key}`} />
      <span className="flex-1 min-w-0 truncate" title={item.original_filename}>{item.original_filename}</span>
      {item.already_exists && <span className="rounded bg-amber-100 text-amber-800 px-1.5 py-0.5" data-testid={`restore-exists-${item.key}`}>{t('restoreFiles.stillExists')}</span>}
      {!item.restorable && <span className="rounded bg-red-100 text-red-700 px-1.5 py-0.5">{t('restoreFiles.missing')}</span>}
      <span className="text-gray-500 w-20 truncate">{item.category || '—'}</span>
      <FileVisibilityBadge attachment={{ ...item, id: `restore-${item.key}`, can_edit_visibility: false }} />
    </div>
  );
};

/** Admin: put files from a backup ZIP back onto a project, customer or employee. */
export const RestoreFromBackupCard = () => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [upload, setUpload] = useState(null);
  const [selected, setSelected] = useState([]);
  const [target, setTarget] = useState({ type: 'project', id: '' });
  const [busy, setBusy] = useState(false);

  const inspect = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/attachments/restore/inspect`, fd);
      setUpload({ ...r.data, name: file.name });
      setSelected(r.data.items.filter((x) => x.restorable && !x.already_exists).map((x) => x.key));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('restoreFiles.inspectFailed'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const discard = () => {
    if (upload) axios.delete(`${API}/admin/attachments/restore/${upload.token}`).catch(() => {});
    setUpload(null);
    setSelected([]);
  };

  const restore = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/attachments/restore`, {
        token: upload.token, keys: selected, entity_type: target.type, entity_id: target.id,
      });
      toast.success(t('restoreFiles.restored', { count: r.data.restored.length }));
      if (r.data.skipped.length) toast.warning(t('restoreFiles.skipped', { count: r.data.skipped.length }));
      discard();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('restoreFiles.restoreFailed'));
    } finally {
      setBusy(false);
    }
  };

  const restorable = upload?.items.filter((x) => x.restorable) || [];
  return (
    <Card data-testid="restore-backup-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><RotateCcw className="h-4 w-4" />{t('restoreFiles.title')}</CardTitle>
        <CardDescription>{t('restoreFiles.desc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <input ref={inputRef} type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => inspect(e.target.files?.[0])} data-testid="restore-zip-input" />
        {!upload ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="restore-zip-pick">
            <Upload className="h-4 w-4 mr-1" />{busy ? t('restoreFiles.reading') : t('restoreFiles.pickZip')}
          </Button>
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium truncate" data-testid="restore-zip-name">{upload.name}</span>
              <span className="text-gray-500" data-testid="restore-summary">{t('restoreFiles.summary', { count: upload.items.length, selected: selected.length })}</span>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={discard} data-testid="restore-discard"><X className="h-3.5 w-3.5" /></Button>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={restorable.length > 0 && selected.length === restorable.length}
                onCheckedChange={(on) => setSelected(on ? restorable.map((x) => x.key) : [])} data-testid="restore-select-all" />
              {t('fileVisibility.selectAllRows')}
            </label>
            <div className="max-h-72 overflow-y-auto rounded border">
              {upload.items.map((x) => (
                <RestoreRow key={x.key} item={x} checked={selected.includes(x.key)}
                  onToggle={(on) => setSelected((p) => on ? [...p, x.key] : p.filter((k) => k !== x.key))} />
              ))}
            </div>
            <p className="text-xs text-gray-600">{t('restoreFiles.target')}</p>
            <RecordPicker value={target} onChange={setTarget} />
            <Button size="sm" disabled={busy || !selected.length || !target.id} onClick={restore} data-testid="restore-confirm">
              <RotateCcw className="h-4 w-4 mr-1" />{t('restoreFiles.restoreButton', { count: selected.length })}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};
